import {useCallback} from 'react';


import {trackUserEvents} from 'src/openapi/admin/apis/TrackUserEventApi';
import {UserEventCode} from 'src/openapi/admin/models/UserEventCode';
import {useAppDispatch} from 'src/store/hooks';

export const useEventTracking = () => {
    const appDispatch = useAppDispatch();

    const rolesOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ROLES_OPEN},
            },
        }));
    }, [appDispatch]);

    const roleOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ROLE_OPEN},
            },
        }));
    }, [appDispatch]);

    const roleCreate = useCallback(({isDuplicate, place}: {isDuplicate: boolean, place: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ROLE_CREATE, isDuplicate, place},
            },
        }));
    }, [appDispatch]);

    const roleDelete = useCallback(({place}: {place: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ROLE_DELETE, place},
            },
        }));
    }, [appDispatch]);

    const roleUpdate = useCallback(({targetRoleId, parameterName}: {targetRoleId: number, parameterName: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ROLE_UPDATE, targetRoleId, parameterName},
            },
        }));
    }, [appDispatch]);

    const groupOpen = useCallback(({viewType}: {viewType: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.GROUP_OPEN, viewType},
            },
        }));
    }, [appDispatch]);

    const groupSubmit = useCallback(({viewType, submit}: {viewType: string, submit: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.GROUP_SUBMIT, viewType, submit},
            },
        }));
    }, [appDispatch]);

    const groupChange = useCallback(({viewType, parameterName}: {viewType?: string, parameterName: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.GROUP_CHANGE, viewType, parameterName},
            },
        }));
    }, [appDispatch]);

    const groupDelete = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.GROUP_DELETE},
            },
        }));
    }, [appDispatch]);

    const bulkOpen = useCallback(({toolName}: {toolName: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.BULK_ACTION_OPEN, toolName},
            },
        }));
    }, [appDispatch]);

    const bulkSubmit = useCallback(({toolName, submit}: {toolName: string, submit: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.BULK_ACTION_SUBMIT, toolName, submit},
            },
        }));
    }, [appDispatch]);

    const bulkDownload = useCallback(({toolName}: {toolName: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.BULK_ACTION_DOWNLOAD, toolName},
            },
        }));
    }, [appDispatch]);

    const licenseCreateOpen = useCallback(({fromExisting}: {fromExisting: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.LICENSE_CREATE_OPEN, fromExisting},
            },
        }));
    }, [appDispatch]);

    const licenseCreateSubmit = useCallback(({submit}: {submit: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.LICENSE_CREATE_SUBMIT, submit},
            },
        }));
    }, [appDispatch]);

    const licenseCreateChange = useCallback(({parameterName}: {parameterName: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.LICENSE_CREATE_CHANGE, parameterName},
            },
        }));
    }, [appDispatch]);

    const accountCreateOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ACCOUNT_CREATE_OPEN},
            },
        }));
    }, [appDispatch]);

    const accountCreateSubmit = useCallback(({action, place}: {action: string, place: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ACCOUNT_CREATE_SUBMIT, action, place},
            },
        }));
    }, [appDispatch]);

    const accountTabChange = useCallback(({activeTab, previousTab}: {activeTab: string, previousTab: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ACCOUNT_TAB_CHANGE, activeTab, previousTab},
            },
        }));
    }, [appDispatch]);

    const accountProfileOpen = useCallback(({place}: {place: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ACCOUNT_PROFILE_OPEN, place},
            },
        }));
    }, [appDispatch]);

    const accountFieldChange = useCallback(({parameterName, section, viewType}: {
        parameterName: string,
        section: string,
        viewType: 'New' | 'Existed',
    }) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ACCOUNT_FIELD_CHANGE, parameterName, section, viewType},
            },
        }));
    }, [appDispatch]);

    const accountSubmit = useCallback(({place, submit}: {place: string, submit: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ACCOUNT_SUBMIT, place, submit},
            },
        }));
    }, [appDispatch]);

    const logout = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.LOGOUT},
            },
        }));
    }, [appDispatch]);

    const searchCriteriaOpen = useCallback(({place}: {place: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.SEARCH_CRITERIA_OPEN, place},
            },
        }));
    }, [appDispatch]);

    const searchCriteriaAccount = useCallback(({parameterName}: {parameterName: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.SEARCH_CRITERIA_ACCOUNT_CHANGE, parameterName},
            },
        }));
    }, [appDispatch]);

    const searchCriteriaUser = useCallback(({parameterName}: {parameterName: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.SEARCH_CRITERIA_USER_CHANGE, parameterName},
            },
        }));
    }, [appDispatch]);

    const searchCriteriaDownload = useCallback(({viewType}: {viewType: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.SEARCH_DOWNLOAD, viewType},
            },
        }));
    }, [appDispatch]);

    const searchCriteriaRun = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.SEARCH_CRITERIA_RUN},
            },
        }));
    }, [appDispatch]);

    const userCreateOpen = useCallback(({place}: {place: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_CREATE_OPEN, place},
            },
        }));
    }, [appDispatch]);
    const userCreateSubmit = useCallback(({place, action}: {place: string, action: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_CREATE_SUBMIT, place, action},
            },
        }));
    }, [appDispatch]);
    const userFieldChange = useCallback(({section, parameterName, viewType}: {
        section: string, parameterName: string, viewType: string,
    }) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_FIELD_CHANGE, section, parameterName, viewType},
            },
        }));
    }, [appDispatch]);
    const userImportContentOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_IMPORT_CONTENT_OPEN},
            },
        }));
    }, [appDispatch]);
    const userImportContentSubmit = useCallback(({submit}: {submit: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_IMPORT_CONTENT_SUBMIT, submit},
            },
        }));
    }, [appDispatch]);
    const userProfileOpen = useCallback(({place}: {place: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_PROFILE_OPEN, place},
            },
        }));
    }, [appDispatch]);
    const userTabChange = useCallback(({activeTab, previousTab}: {activeTab: string, previousTab: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_TAB_CHANGE, activeTab, previousTab},
            },
        }));
    }, [appDispatch]);
    const userSubmit = useCallback(({place, submit}: {place: string, submit: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_SUBMIT, place, submit},
            },
        }));
    }, [appDispatch]);

    const unsavedChanges = useCallback(({action}: {action: 'Leave Page' | 'Cancel'}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.UNSAVED_CHANGES, action},
            },
        }));
    }, [appDispatch]);

    const userRoleUpdate = useCallback(({parameterName, targetUserId}:
    {parameterName: string, targetUserId: number}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_ROLE_UPDATE, parameterName, targetUserId},
            },
        }));
    }, [appDispatch]);

    const loginLinkGeneratorOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.LOGIN_LINK_GENERATOR_OPEN},
            },
        }));
    }, [appDispatch]);

    const loginLinkGeneratorDownload = useCallback(({success}: {success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.LOGIN_LINK_GENERATOR_DOWNLOAD,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const accountReportRun = useCallback(({reportType, success}: {reportType: string, success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.ACCOUNT_REPORT_RUN,
                    reportType,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const manageEntitlementsOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ENT_OPEN},
            },
        }));
    }, [appDispatch]);

    const createRoleOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ENT_CR_OPEN},
            },
        }));
    }, [appDispatch]);

    const createRoleSubmit = useCallback(({submit, success}: {submit: boolean, success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.ENT_CR_SUBMIT,
                    submit,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const editRoleOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ENT_ER_OPEN},
            },
        }));
    }, [appDispatch]);

    const editRoleSubmit = useCallback(({submit, success}: {submit: boolean, success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.ENT_ER_SUBMIT,
                    submit,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const createGrantOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ENT_CG_OPEN},
            },
        }));
    }, [appDispatch]);

    const createGrantSubmit = useCallback(({submit, success}: {submit: boolean, success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.ENT_CG_SUBMIT,
                    submit,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const editGrantOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ENT_EG_OPEN},
            },
        }));
    }, [appDispatch]);

    const editGrantSubmit = useCallback(({submit, success}: {submit: boolean, success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.ENT_EG_SUBMIT,
                    submit,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const createResourceOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ENT_CRES_OPEN},
            },
        }));
    }, [appDispatch]);

    const createResourceSubmit = useCallback(({submit, success}: {submit: boolean, success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.ENT_CRES_SUBMIT,
                    submit,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const editResourceOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ENT_ERES_OPEN},
            },
        }));
    }, [appDispatch]);

    const editResourceSubmit = useCallback(({submit, success}: {submit: boolean, success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.ENT_ERES_SUBMIT,
                    submit,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const createConferenceOpen = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.CC_OPEN},
            },
        }));
    }, [appDispatch]);

    const editConferenceSubmit = useCallback(({submit, success}: {submit: boolean, success: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.CC_SUBMIT,
                    submit,
                    success,
                },
            },
        }));
    }, [appDispatch]);

    const loginActionSubmit = useCallback((
        {submit, success, itemAction}: {submit: boolean, success: boolean, itemAction: string},
    ) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.LA_SUBMIT,
                    submit,
                    success,
                    itemAction,
                },
            },
        }));
    }, [appDispatch]);

    const accountAuditLogSubmit = useCallback((
        {actionType, filterCriteria}: {actionType: string, filterCriteria: string[]},
    ) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {
                    eventCode: UserEventCode.ACCOUNT_AL_FILTER_SUBMIT,
                    actionType,
                    filterCriteria,
                },
            },
        }));
    }, [appDispatch]);

    const userContentTabChange = useCallback(({activeTab, previousTab}: {activeTab: string, previousTab: string}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.USER_CONTENT_TAB_CHANGE, activeTab, previousTab},
            },
        }));
    }, [appDispatch]);

    const mcpTokenRefresh = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ADMIN_MCP_TOKEN_REFRESH},
            },
        }));
    }, [appDispatch]);

    const mcpTokenStatusChange = useCallback(({enabled}: {enabled: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ADMIN_MCP_TOKEN_STATUS_CHANGE, enabled},
            },
        }));
    }, [appDispatch]);

    const mcpContrCreate = useCallback(() => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ADMIN_MCP_CONTR_CREATE},
            },
        }));
    }, [appDispatch]);

    const mcpContrUpdate = useCallback(({changedFields}: {changedFields: string[]}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ADMIN_MCP_CONTR_UPDATE, changedFields},
            },
        }));
    }, [appDispatch]);

    const mcpAccessTgl = useCallback(({enabled}: {enabled: boolean}) => {
        appDispatch(trackUserEvents({
            userEventRequest: {
                userEvent: {eventCode: UserEventCode.ADMIN_MCP_ACCESS_TGL, enabled},
            },
        }));
    }, [appDispatch]);

    return {
        rolesOpen,
        roleOpen,
        roleCreate,
        roleUpdate,
        roleDelete,

        licenseCreateOpen,
        licenseCreateSubmit,
        licenseCreateChange,

        groupOpen,
        groupChange,
        groupDelete,
        groupSubmit,

        bulkOpen,
        bulkSubmit,
        bulkDownload,

        accountCreateOpen,
        accountCreateSubmit,
        accountTabChange,
        accountProfileOpen,
        accountFieldChange,
        accountSubmit,

        logout,
        searchCriteriaOpen,
        searchCriteriaAccount,
        searchCriteriaUser,
        searchCriteriaRun,
        searchCriteriaDownload,

        userCreateOpen,
        userCreateSubmit,
        userFieldChange,
        userRoleUpdate,
        userImportContentOpen,
        userImportContentSubmit,
        userProfileOpen,
        userTabChange,
        userSubmit,
        unsavedChanges,

        loginLinkGeneratorOpen,
        loginLinkGeneratorDownload,
        accountReportRun,

        manageEntitlementsOpen,
        createRoleOpen,
        createRoleSubmit,
        editRoleOpen,
        editRoleSubmit,
        createGrantOpen,
        createGrantSubmit,
        editGrantOpen,
        editGrantSubmit,
        createResourceOpen,
        createResourceSubmit,
        editResourceSubmit,
        editResourceOpen,

        createConferenceOpen,
        editConferenceSubmit,

        loginActionSubmit,
        accountAuditLogSubmit,

        userContentTabChange,

        mcpTokenRefresh,
        mcpTokenStatusChange,
        mcpContrCreate,
        mcpContrUpdate,
        mcpAccessTgl,
    };
};
